import { useCallback, useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

function AttendanceForm({ open, onOpenChange, editing, defaultDate, employees, onSaved, isAdmin }) {
  const [form, setForm] = useState({ employee_id: "", date: defaultDate, check_in: "", check_out: "", active_hours: "", idle_hours: "", mode: "present", notes: "" });

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        employee_id: editing.employee_id, date: editing.date,
        check_in: editing.check_in || "", check_out: editing.check_out || "",
        active_hours: editing.active_hours != null ? String(editing.active_hours) : "",
        idle_hours: editing.idle_hours != null ? String(editing.idle_hours) : "",
        mode: editing.status || "present", notes: editing.notes || "",
      } : { employee_id: "", date: defaultDate, check_in: `${defaultDate}T09:00`, check_out: `${defaultDate}T18:00`, active_hours: "", idle_hours: "", mode: "present", notes: "" });
    }
  }, [open, editing, defaultDate]);

  const save = async () => {
    if (!form.employee_id || !form.date) return toast.error("Employee and date required");
    const active = form.active_hours === "" ? null : Number(form.active_hours);
    const idle = form.idle_hours === "" ? null : Number(form.idle_hours);
    if (active != null && (isNaN(active) || active < 0)) return toast.error("Active hours must be ≥ 0");
    if (idle != null && (isNaN(idle) || idle < 0)) return toast.error("Idle hours must be ≥ 0");
    const payload = {
      check_in: form.check_in ? new Date(form.check_in).toISOString() : null,
      check_out: form.check_out ? new Date(form.check_out).toISOString() : null,
      active_hours: active,
      idle_hours: idle,
      mode: form.mode,
      notes: form.notes,
    };
    try {
      if (editing) await api.patch(`/attendance/${editing.id}`, payload);
      else await api.post("/attendance", { employee_id: form.employee_id, date: form.date, ...payload });
      toast.success(editing ? "Updated" : "Recorded");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit Attendance" : "Record Attendance"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div>
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="att-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!!editing} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Clock In</Label><Input type="datetime-local" data-testid="att-in" value={(form.check_in || "").slice(0, 16)} onChange={(e) => setForm({ ...form, check_in: e.target.value })} /></div>
            <div><Label>Clock Out</Label><Input type="datetime-local" data-testid="att-out" value={(form.check_out || "").slice(0, 16)} onChange={(e) => setForm({ ...form, check_out: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Active Hours</Label>
              <Input type="number" min="0" step="0.01" data-testid="att-active" placeholder="e.g. 7.5" value={form.active_hours} onChange={(e) => setForm({ ...form, active_hours: e.target.value })} />
            </div>
            <div>
              <Label>Idle Hours</Label>
              <Input type="number" min="0" step="0.01" data-testid="att-idle" placeholder="e.g. 1.6" value={form.idle_hours} onChange={(e) => setForm({ ...form, idle_hours: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="att-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="present">WFO</SelectItem>
                <SelectItem value="wfh">WFH</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground">Extra / Less is computed against <b>Active Hours</b> (WFH 8-9h, WFO 7-8h). If Active Hours is left blank, elapsed clock time is used.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="att-save" onClick={save}>{editing ? "Save" : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isBranchAdmin = user?.role === "branch_admin";
  // Tech Leads and ICs (individual contributors — anyone who isn't
  // Manager/Head/Director/Admin) don't record attendance from this page.
  const canRecord = (() => {
    if (!user) return false;
    if (user.role === "admin" || user.role === "branch_admin") return true;
    const d = (user.designation || "").toLowerCase();
    if (d.includes("tech lead")) return false;
    return d.includes("manager") || d.includes("head") || d.includes("director");
  })();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/attendance/by-date?date=${date}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [date]);
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this attendance record?")) return;
    try { await api.delete(`/attendance/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "employee.full_name", label: "Employee", sortable: true, render: (r) => (
      <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(r.employee?.avatar_url)} /><AvatarFallback className="text-[9px]">{(r.employee?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
        <div><div className="font-medium">{r.employee?.full_name}</div><div className="text-[10px] text-muted-foreground">{r.employee?.department}</div></div>
      </div>
    )},
    { key: "date", label: "Date", sortable: true, className: "number" },
    { key: "check_in", label: "Clock In", sortable: true, render: (r) => r.check_in ? new Date(r.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—" },
    { key: "check_out", label: "Clock Out", sortable: true, render: (r) => r.check_out ? new Date(r.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—" },
    { key: "is_late", label: "Late", sortable: true, render: (r) => <Badge variant={r.is_late ? "destructive" : "outline"} className="text-[10px]">{r.is_late ? "Yes" : "No"}</Badge> },
    { key: "is_half_day", label: "Half Day", sortable: true, render: (r) => <Badge variant={r.is_half_day ? "secondary" : "outline"} className="text-[10px]">{r.is_half_day ? "Yes" : "No"}</Badge> },
    { key: "mode_display", label: "Mode", sortable: true, render: (r) => <Badge variant="outline" className="text-[10px]">{r.mode_display}</Badge> },
    { key: "active_hours", label: "Active Hours", sortable: true, className: "number", render: (r) => (
      <span className="number">{(r.active_hours || 0).toFixed(2)}</span>
    )},
    { key: "idle_hours", label: "Idle Hours", sortable: true, className: "number", render: (r) => (
      <span className="number text-muted-foreground">{(r.idle_hours || 0).toFixed(2)}</span>
    )},
    { key: "extra_less_display", label: "Extra / Less", sortable: true, render: (r) => (
      <span className={`number ${r.extra_less_hours > 0 ? "text-emerald-500" : r.extra_less_hours < 0 ? "text-red-500" : "text-muted-foreground"}`}>{r.extra_less_display}</span>
    )},
  ];

  const rowActions = useCallback((r) => {
    // Edit is only for someone else's record (and only when the viewer is
    // allowed to record attendance in the first place — manager/admin/head).
    // Employees must never edit their own clock-in/out from this page.
    const isSelf = r.employee_id === user?.id;
    // Branch Admins record for their own branch and may only change/delete records they created.
    const ownedByMe = r.created_by === user?.id;
    const canEdit = isAdmin || (isBranchAdmin ? ownedByMe : (canRecord && !isSelf));
    const canDelete = isAdmin || (isBranchAdmin && ownedByMe);
    if (!canEdit && !canDelete) return null;
    return (
      <div className="flex justify-end gap-1">
        {canEdit && (
          <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`att-edit-${r.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {canDelete && (
          <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`att-del-${r.id}`}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
    );
  }, [isAdmin, isBranchAdmin, canRecord, user?.id]);

  return (
    <div className="space-y-6">
      <PageHeader
        testid="attendance-header" title="Attendance" subtitle={`Date-wise view · ${rows.length} records`}
        actions={
          <div className="flex gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="att-date" />
            {canRecord && (
              <Button data-testid="att-new" onClick={() => { setEditing(null); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-1" />Record</Button>
            )}
          </div>
        }
      />
      <DataTable
        testid="dt-att"
        loading={loading}
        empty={`No attendance for ${date}`}
        columns={columns}
        data={rows}
        searchKeys={["employee.full_name", "employee.department"]}
        rowActions={rowActions}
      />
      <AttendanceForm open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} defaultDate={date} employees={isBranchAdmin ? employees.filter((e) => e.branch_id === user?.branch_id) : employees} onSaved={load} isAdmin={isAdmin} />
    </div>
  );
}
